'use client';

import React from 'react';
import ReactDOM from 'react-dom/client';

// Atoms
import { Button, ButtonProps } from '../atoms/Button';
import { Slider, SliderProps } from '../atoms/Slider';
import { Spinner, SpinnerProps } from '../atoms/Spinner';
import { Text, TextProps } from '../atoms/Text';
import { Tooltip, TooltipProps } from '../atoms/Tooltip';

// Molecules
import { Accordion, AccordionProps } from '../molecules/Accordion';
import { Breadcrumbs, BreadcrumbsProps } from '../molecules/Breadcrumbs';
import { Stepper, StepperProps } from '../molecules/Stepper';
import { Tabs, TabsProps } from '../molecules/Tabs';

// Organisms
import { Footer, FooterProps } from '../organisms/Footer';
import { MainNav, MainNavProps } from '../organisms/MainNav';
import { Modal, ModalProps } from '../organisms/Modal';
import { Page, PageProps } from '../organisms/Page';
import { Sidebar, SidebarProps } from '../organisms/Sidebar';
import { TopNav, TopNavProps } from '../organisms/TopNav';

/**
 * Mount a Button component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Button props
 * @returns The React root instance
 */
export function mountButton(elementOrId: string | HTMLElement, props: ButtonProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a span wrapper to allow multiple components in the same parent
  // Use display: contents to make the wrapper invisible to layout
  const wrapper = document.createElement('span');
  wrapper.style.display = 'contents';
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(Button, props));
  return root;
}

/**
 * Mount a Text component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Text props
 * @returns The React root instance
 */
export function mountText(elementOrId: string | HTMLElement, props: TextProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a span wrapper to allow multiple components in the same parent
  // Use display: contents to make the wrapper invisible to layout
  const wrapper = document.createElement('span');
  wrapper.style.display = 'contents';
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(Text, props));
  return root;
}

/**
 * Mount a Breadcrumbs component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Breadcrumbs props
 * @returns The React root instance
 */
export function mountBreadcrumbs(elementOrId: string | HTMLElement, props: BreadcrumbsProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the breadcrumbs component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(Breadcrumbs, props));
  return root;
}

export interface MountedComponent<P> {
  unmount: () => void;
  update: (newProps: Partial<P>) => void;
}

/**
 * Mount a Sidebar component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Sidebar props
 * @returns Object with unmount and update methods
 */
export function mountSidebar(
  elementOrId: string | HTMLElement,
  props: SidebarProps
): MountedComponent<SidebarProps> | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the sidebar component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: SidebarProps) => {
    root.render(React.createElement(Sidebar, p));
  };

  render(currentProps);

  return {
    unmount: () => root.unmount(),
    update: (newProps: Partial<SidebarProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
  };
}

/**
 * Mount a TopNav component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - TopNav props
 * @returns The React root instance
 */
export function mountTopNav(elementOrId: string | HTMLElement, props: TopNavProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the topnav component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(TopNav, props));
  return root;
}

/**
 * Mount a MainNav component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - MainNav props
 * @returns The React root instance
 */
export function mountMainNav(elementOrId: string | HTMLElement, props: MainNavProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the mainnav component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(MainNav, props));
  return root;
}

/**
 * Mount a Footer component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Footer props
 * @returns The React root instance
 */
export function mountFooter(elementOrId: string | HTMLElement, props: FooterProps) {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the footer component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  root.render(React.createElement(Footer, props));
  return root;
}

/**
 * Mount a Page component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Page props
 * @returns Object with unmount and update methods
 */
export function mountPage(
  elementOrId: string | HTMLElement,
  props: PageProps
): MountedComponent<PageProps> | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the page component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: PageProps) => {
    root.render(React.createElement(Page, p));
  };

  render(currentProps);

  return {
    unmount: () => root.unmount(),
    update: (newProps: Partial<PageProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
  };
}

export interface MountedModal {
  /**
   * Open the modal
   */
  open: () => void;
  /**
   * Close the modal
   */
  close: () => void;
  /**
   * Check if the modal is currently open
   */
  isOpen: () => boolean;
  /**
   * Update the modal props (excluding isOpen and onClose)
   */
  update: (newProps: Partial<Omit<ModalProps, 'isOpen' | 'onClose'>>) => void;
  /**
   * Unmount and clean up the modal
   */
  unmount: () => void;
}

/**
 * Mount a Modal component that can be controlled imperatively
 * @param props - Modal props (isOpen and onClose are managed internally)
 * @returns Object with open, close, isOpen, update, and unmount methods
 */
export function mountModal(
  props: Omit<ModalProps, 'isOpen' | 'onClose'> & { onClose?: () => void }
): MountedModal {
  // Create a container for the React root
  const container = document.createElement('div');
  container.setAttribute('data-hi-ui-modal-container', '');
  document.body.appendChild(container);

  const root = ReactDOM.createRoot(container);
  let currentProps = { ...props };
  let isOpenState = false;

  const render = () => {
    root.render(
      React.createElement(Modal, {
        ...currentProps,
        isOpen: isOpenState,
        onClose: () => {
          isOpenState = false;
          currentProps.onClose?.();
          render();
        },
      })
    );
  };

  render();

  return {
    open: () => {
      isOpenState = true;
      render();
    },
    close: () => {
      isOpenState = false;
      render();
    },
    isOpen: () => isOpenState,
    update: (newProps) => {
      currentProps = { ...currentProps, ...newProps };
      render();
    },
    unmount: () => {
      root.unmount();
      container.remove();
    },
  };
}

/**
 * Unmount a component from its container
 * @param root - The React root instance returned from mount function
 */
export function unmount(root: ReactDOM.Root | null) {
  if (root) {
    root.unmount();
  }
}

export interface MountedTabs {
  /**
   * Update the tabs props
   */
  update: (newProps: Partial<TabsProps>) => void;
  /**
   * Unmount and clean up the tabs
   */
  unmount: () => void;
}

/**
 * Mount a Tabs component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Tabs props
 * @returns Object with update and unmount methods
 */
export function mountTabs(elementOrId: string | HTMLElement, props: TabsProps): MountedTabs | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the tabs component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: TabsProps) => {
    root.render(React.createElement(Tabs, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<TabsProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}

export interface MountedStepper {
  /**
   * Update the stepper props
   */
  update: (newProps: Partial<StepperProps>) => void;
  /**
   * Unmount and clean up the stepper
   */
  unmount: () => void;
}

/**
 * Mount a Stepper component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Stepper props
 * @returns Object with update and unmount methods
 */
export function mountStepper(
  elementOrId: string | HTMLElement,
  props: StepperProps
): MountedStepper | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the stepper component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: StepperProps) => {
    root.render(React.createElement(Stepper, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<StepperProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}

export interface MountedAccordion {
  /**
   * Update the accordion props
   */
  update: (newProps: Partial<AccordionProps>) => void;
  /**
   * Unmount and clean up the accordion
   */
  unmount: () => void;
}

/**
 * Mount an Accordion component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Accordion props
 * @returns Object with update and unmount methods
 */
export function mountAccordion(
  elementOrId: string | HTMLElement,
  props: AccordionProps
): MountedAccordion | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the accordion component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: AccordionProps) => {
    root.render(React.createElement(Accordion, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<AccordionProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}

export interface MountedSpinner {
  /**
   * Update the spinner props
   */
  update: (newProps: Partial<SpinnerProps>) => void;
  /**
   * Unmount and clean up the spinner
   */
  unmount: () => void;
}

/**
 * Mount a Spinner component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Spinner props
 * @returns Object with update and unmount methods
 */
export function mountSpinner(
  elementOrId: string | HTMLElement,
  props: SpinnerProps
): MountedSpinner | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a span wrapper for the spinner component
  const wrapper = document.createElement('span');
  wrapper.style.display = 'contents';
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: SpinnerProps) => {
    root.render(React.createElement(Spinner, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<SpinnerProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}

export interface MountedTooltip {
  /**
   * Update the tooltip props
   */
  update: (newProps: Partial<TooltipProps>) => void;
  /**
   * Unmount and clean up the tooltip
   */
  unmount: () => void;
}

/**
 * Mount a Tooltip component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Tooltip props
 * @returns Object with update and unmount methods
 */
export function mountTooltip(
  elementOrId: string | HTMLElement,
  props: TooltipProps
): MountedTooltip | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a span wrapper for the tooltip component
  const wrapper = document.createElement('span');
  wrapper.style.display = 'contents';
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: TooltipProps) => {
    root.render(React.createElement(Tooltip, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<TooltipProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}

export interface MountedSlider {
  /**
   * Update the slider props
   */
  update: (newProps: Partial<SliderProps>) => void;
  /**
   * Unmount and clean up the slider
   */
  unmount: () => void;
}

/**
 * Mount a Slider component to a DOM element
 * @param elementOrId - The DOM element or the ID of the element to mount to
 * @param props - Slider props
 * @returns Object with update and unmount methods
 */
export function mountSlider(
  elementOrId: string | HTMLElement,
  props: SliderProps
): MountedSlider | null {
  const parent =
    typeof elementOrId === 'string' ? document.getElementById(elementOrId) : elementOrId;

  if (!parent) {
    console.error(
      `[HIUI] Element ${typeof elementOrId === 'string' ? `with id "${elementOrId}"` : ''} not found`
    );
    return null;
  }

  // Create a div wrapper for the slider component
  const wrapper = document.createElement('div');
  parent.appendChild(wrapper);

  const root = ReactDOM.createRoot(wrapper);
  let currentProps = { ...props };

  const render = (p: SliderProps) => {
    root.render(React.createElement(Slider, p));
  };

  render(currentProps);

  return {
    update: (newProps: Partial<SliderProps>) => {
      currentProps = { ...currentProps, ...newProps };
      render(currentProps);
    },
    unmount: () => {
      root.unmount();
      wrapper.remove();
    },
  };
}
